import { useAuth } from "~/store/auth";
import { useBetbook } from "~/store/betbook";
import { useBetslip } from "~/store/betslip";
import { useBonusses } from "~/store/bonusses";
import { useSettings } from "~/store/settings";
import type { IAuthAuthenticate } from "~/types/auth/authenticate";
import type { ISitemaps } from "~/types/kentico/sitemaps";
import type { ICasinoBonusSummary } from "~/types/player/casino-bonus-summary";
import type { ICasinoBonusSummaryOld } from "~/types/player/casino-bonus-summary-old";
import type { ISportBonusSummary } from "~/types/player/sport-bonus-summary";
import api from "~/utils/auth/api";
import t from "~/utils/translate";

declare global {
	interface Window {
		webkit: any;
		Data: any;
		promptLogin: Function;
		launchDeposits: Function;
		launchFeeds: Function;
		launchWithdrawals: Function;
		launchMyBets: Function;
		launchTransactionHistory: Function;
		launchBonusSummary: Function;
		launchPromoVoucher: Function;
		launchUpdateDetails: Function;
		launchResponsibleGaming: Function;
		launchDocumentVerification: Function;
		launchManagePassword: Function;
		launchPublicProfile: Function;
		launchChat: Function;
		launchInfuencer: Function;
		openBetslip: Function;
		strikeBet: Function;
		launchReg: Function;
		login: Function;
		refreshBalances: Function;
		launchSearch: Function;
		logout: Function;
		changeOdds: Function;
		changeTheme: Function;
		changeLanguage: Function;
		navigate: Function;
		launchBigWinners: Function;
		openTopEvents: Function;
		openAtoZMenu: Function;
		launchFeaturedLeagues: Function;
		openInbox: Function;
		appsFlyerInit: Function;
		launchNotificationPanel: Function;
		loginWithAuthToken: Function;
	}
}

export default defineNuxtPlugin((_nuxtApp) => {
	const messageHandler = ({ message, data }: { message: string; data: any }) => {
		const settings = useSettings();

		if (!settings.isNativeApp || !Boolean(window)) return;

		try {
			switch (settings.appType?.toUpperCase()) {
				case "IOS":
					window.webkit.messageHandlers.PostData.postMessage(JSON.stringify({ message, data }));
					break;
				default:
					window.Data.postData(JSON.stringify({ message, data }));
					break;
			}
		} catch (error) {
			console.error({ appType: settings.appType, message, data, error });
		}
	};

	const promptLogin = () => {
		messageHandler({
			message: "login",
			data: { value: "showLoginPopUp" },
		});
	};

	window.changeTheme = (theme: "dark" | "light" | "system") => {
		const colorMode = useColorMode();
		colorMode.preference = theme;
	};

	window.promptLogin = () => promptLogin();

	window.launchDeposits = () => {
		openAccountModal({ account: "deposit" });
	};

	window.launchFeeds = () => {
		openAccountModal({ "betway-feed": "true" });
	};

	window.launchWithdrawals = () => {
		openAccountModal({ account: "withdraw" });
	};

	window.launchMyBets = () => {
		openAccountModal({ account: "my-bets" });
	};

	window.launchInfuencer = () => {
		openAccountModal({ account: "booking-codes" });
	};

	window.launchTransactionHistory = () => {
		openAccountModal({ account: "transaction-history" });
	};

	window.launchBonusSummary = () => {
		openAccountModal({ account: "bonus-summary" });
	};

	window.launchPromoVoucher = () => {
		openAccountModal({ account: "promo-voucher" });
	};

	window.launchUpdateDetails = () => {
		openAccountModal({ account: "update-details" });
	};

	window.launchResponsibleGaming = () => {
		openAccountModal({ account: "responsible-gaming" });
	};

	window.launchDocumentVerification = () => {
		openAccountModal({ account: "document-verification" });
	};

	window.launchManagePassword = () => {
		openAccountModal({ account: "change-password" });
	};

	window.launchPublicProfile = () => {
		openAccountModal({ account: "public-profile" });
	};

	window.openInbox = () => {
		const auth = useAuth();
		auth.modals.notifications = true;
	};

	window.launchChat = () => {
		openLiveChat();
	};

	window.launchBigWinners = () => {
		openAccountModal({ "big-winners": "true" }, false);
	};

	window.openTopEvents = () => {
		openAccountModal({ "top-events": "true" }, false);
	};

	window.launchFeaturedLeagues = () => {
		openAccountModal({ "top-events": "true" }, false);
	};

	window.openAtoZMenu = () => {
		openAccountModal({ "a-z-sports": "true" }, false);
	};

	window.launchReg = () => {
		const auth = useAuth();
		auth.modals.register = true;
	};

	window.openBetslip = () => {
		openBetslip();
	};

	window.strikeBet = () => {
		openBetslip();
	};

	window.loginWithAuthToken = async (access_token: string, refresh_token: string) => {
		const auth = useAuth();
		await auth.login(access_token, refresh_token);
	};

	window.refreshBalances = async () => {
		const auth = useAuth();
		const settings = useSettings();
		const bonusses = useBonusses();
		let rewardsBalance = 0;

		if (!auth.loggedIn) return promptLogin();

		const sportsBonusEnabled = settings.hasFeature("IsSportsBonusEnabled");
		const casinoBonusEnabled = settings.hasFeature("IsShowCasinoBonusEnabled");
		const isNewCasinoBonus = settings.hasFeature("IsNewCasinoBonus");

		await auth.updateBalance();

		if (sportsBonusEnabled || casinoBonusEnabled) {
			try {
				const res = await api<{ sportBonusSummaries: ISportBonusSummary[]; playthroughBonusSummary: ICasinoBonusSummaryOld }>({
					url: "/PlayThroughUserBonus/GetUserBonusSummary",
					type: "player",
					apiVersion: "v2",
					options: { method: "GET" },
				});

				if (sportsBonusEnabled) bonusses.sportBonusSummaries = res.sportBonusSummaries;
				if (casinoBonusEnabled) {
					if (!isNewCasinoBonus) {
						bonusses.casinoBonusSummary = res.playthroughBonusSummary;
					} else if (casinoBonusEnabled) {
						const activeBonus = await api<ICasinoBonusSummary>({
							url: `/CasinoBonus/player/${auth.profile?.id}/active`,
							type: "casinoBonus",
							options: { method: "GET" },
						});
						bonusses.casinoBonusSummary = activeBonus;
					}
				}
			} catch (error) {
				console.error(error);
			}
		}

		if (settings.hasFeature("IsLoyaltyEnabled") && settings.isNativeApp) {
			const res = await api<{ amount: number }>({
				url: "/Balance",
				type: "loyalty",
				options: { method: "GET" },
			});
			rewardsBalance = res.amount;
		}

		const currencySymbol = settings.config?.appSetting?.CurrencySymbol;

		const playthroughBalancePercent = bonusses.casinoBonusSummary
			? isNewCasinoBonus
				? Math.min(
						((bonusses.casinoBonusSummary as ICasinoBonusSummary).totalProgressAmount /
							(bonusses.casinoBonusSummary as ICasinoBonusSummary).totalTargetAmount) *
							100,
						100
				  )
				: Math.min(
						((bonusses.casinoBonusSummary as ICasinoBonusSummaryOld)?.playthroughProgressAmount /
							(bonusses.casinoBonusSummary as ICasinoBonusSummaryOld)?.playthroughTargetAmount) *
							100,
						100
				  )
			: 0;

		const data = {
			cashBalance: auth.balance?.cashBalance,
			freeBetBalance: auth.balance?.bonusBalance,
			bonusBalance: auth.balance?.ticketBalance,
			rewardsBalance: rewardsBalance,
			sportBonusBalance: auth.balance?.sportBonusBalance,
			playthroughBalance: auth.balance?.playthroughBalance,
			sportBonusBalancePercent: bonusses.activeSportBonus
				? Math.min((bonusses.activeSportBonus?.playthroughProgressAmount / bonusses.activeSportBonus?.playthroughTargetAmount) * 100, 100)
				: 0,
			playthroughBalancePercent,
			sportInactivebonuses: bonusses.sportBonusSummaries?.filter((bonus) => bonus.status.toLowerCase() === "inactive"),
			currencySymbol,
			tickets: [
				{
					key: "betwayJackpot",
					count: auth.balance?.betwayJackpotTicketBalance,
				},
				{
					key: "dailyJackpot",
					count: auth.balance?.dailyJackpotTicketBalance,
				},
			],
		};

		messageHandler({
			message: "Balance",
			data,
		});
	};

	window.launchSearch = () => {
		const settings = useSettings();
		settings.showSearch = true;
	};

	window.logout = async () => {
		const auth = useAuth();
		await auth.logout();
	};

	window.launchNotificationPanel = () => {
		useAuth().modals.notifications = true;
	};

	window.appsFlyerInit = async (appsFlyerData: string) => {
		useSettings().appsFlyerData = appsFlyerData;
	};

	window.login = async (userData: { AnonymousId: string; Username: string; Password: string; CountryCode: string }) => {
		const auth = useAuth();
		const settings = useSettings();
		const sessionMetadata = sessionStorage.getItem("sessionMetadata") || "{}";
		const { $trackEvent } = useNuxtApp();
		if (auth.loggedIn) await auth.logout();

		try {
			const res = await api<IAuthAuthenticate>({
				url: "/users/authenticate",
				type: "auth",
				data: {
					username: userData.Username,
					password: userData.Password,
					countryCode: settings.config?.appSetting?.CountryCode,
					sessionMetadata: JSON.parse(sessionMetadata),
				},
				options: {
					method: "POST",
				},
			});

			await auth.login(res.access_token, res.refresh_token);
			await window.refreshBalances();
			$trackEvent.loginComplete(formatMobile(auth.profile.username));
		} catch (error: any) {
			if (error.errorCode) {
				messageHandler({
					message: "Login",
					data: {
						login: false,
						PlayerId: "",
						error: t(`log-in-errors.${error.errorCode.toString()}`),
					},
				});
			}

			if (!settings.isNativeApp) {
				throw error;
			}
		}
	};

	window.changeOdds = (oddsFormat: "decimal" | "fractional") => {
		const betbook = useBetbook();
		betbook.oddsFormat = oddsFormat;
	};

	window.changeLanguage = async (localeCode: string) => {
		const settings = useSettings();

		const code = localeCode.toLowerCase();
		const locale = settings.config?.cultures.find((culture) => culture.cultureCode.toLowerCase().includes(code));

		if (locale) {
			const translations = await api<{
				[x: string]: string;
			}>({ url: `/cron/locales/synapse/${locale?.cultureCode}`, type: "config" });
			settings.translations = translations;
			const sitemaps = await api<ISitemaps>({
				url: `/cron/sitemaps/synapseV2/{brand}/${locale?.cultureCode}`,
				type: "config",
			});
			settings.sitemap = sitemaps.siteMap[0].data.sitemap;
			settings.locale = locale.cultureCode;
		}
	};

	window.navigate = (routeArg: string) => {
		const router = useRouter();
		const route = useRoute();
		const settings = useSettings();

		const navigateToNativeLobby = routeArg.includes("lobby") && settings.hasNativeLobby;

		if (!navigateToNativeLobby) {
			if (routeArg) {
				router.push(routeArg);
			} else {
				routeArg = route.path;
			}
		}

		const vertical = navigateToNativeLobby && settings.hasNativeLobby ? routeArg.split("/")[2] : null;

		// messageHandler({
		// 	message: "Navigate",
		// 	data: { path: routeArg, vertical },
		// });
	};

	const openAccountModal = (query: { [key: string]: string }, requireAuth = true) => {
		const auth = useAuth();
		const router = useRouter();
		const route = useRoute();

		if (!auth.loggedIn && requireAuth) return promptLogin();

		router.push({ query: { ...route.query, ...query } });
	};

	const openBetslip = () => {
		const betslip = useBetslip();
		betslip.open = true;
	};

	return {
		provide: {
			messageHandler: messageHandler,
		},
	};
});
